import { Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import type { ProductSummary } from "@/content/schema";
import { fr } from "@/i18n/fr";
import { selectStep, selectStepCount, useViewerStore } from "@/state/store";
import { AccountMenu } from "./AccountMenu";
import { ProductSearch } from "./ProductSearch";
import { ThemeToggle } from "./ThemeToggle";

interface HeaderProps {
	/** Catalogue entry of the product, shown until its steps are loaded. */
	product?: ProductSummary;
}

export function Header({ product }: HeaderProps) {
	const data = useViewerStore((s) => s.data);
	const step = useViewerStore(selectStep);
	const total = useViewerStore(selectStepCount);
	const n = step ? Math.min(total, step.num) : 0;
	const guide = data?.product;
	const title =
		guide?.name ??
		(product ? `${product.name} – ${product.designation}` : fr.appTitle);
	const subtitle = [
		guide?.size_cm ? `${guide.size_cm.join(" × ")} cm` : null,
		guide?.notice ? `notice ${guide.notice}` : null,
	]
		.filter(Boolean)
		.join(" · ");
	return (
		<header className="header">
			<Link
				to="/catalogue"
				className="pill shrink-0 no-underline"
				title={fr.catalog}
				aria-label={fr.catalog}
			>
				<ChevronLeft aria-hidden="true" />
				<span className="hidden md:inline">{fr.catalog}</span>
			</Link>
			<div className="brand">
				{title}
				<small>{subtitle}</small>
			</div>
			<div className="spacer" />
			<span className="hidden md:block">
				<ProductSearch />
			</span>
			{data && (
				<div className="progress" aria-live="polite">
					<span>{fr.progress(n, total)}</span>
					<div className="bar">
						<i style={{ width: `${total ? (100 * n) / total : 0}%` }} />
					</div>
				</div>
			)}
			<span className="hidden md:inline-flex">
				<ThemeToggle />
			</span>
			<span className="hidden md:inline-flex">
				<AccountMenu compact />
			</span>
		</header>
	);
}
